import DiscoverFeedInterests from "@components/DiscoverFeedInterests/DiscoverFeedInterests";
import { Icon, SingleSelectDropdown, TextInput } from "@components/library";
import KeywordsDropdown from "@components/library/Dropdowns/KeywordsDropdown/KeywordsDropdown";
import { COLORS, FONTS } from "@constants";
import { useQuery } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { SponsorRole } from "@tsTypes/sponsorProfiles";
import { track } from "@utils/appUtils";
import { getOptionFromValue, getValueFromOption } from "@utils/dropdownUtils";
import { isValidURL } from "@utils/textUtils";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import api from "src/requests/request";
import styled from "styled-components";
import { useCompanyOptions } from "../Hooks";
import { BaseOnboardingProfileInfo } from "../OnboardingContainer";
import * as S from "../StepStyles";
import { COMPANY_SPONSOR_TITLE_LIMIT } from "./Step3";

export type CompanySponsorOnboardingProfileInfo = BaseOnboardingProfileInfo &
  Partial<{
    company: string;
    companyURL: string;
    role: string | undefined;
    title: string;
    areasOfExpertise: string[];
  }>;

interface Props {
  profileInfo: CompanySponsorOnboardingProfileInfo;
  setProfileInfo: Dispatch<SetStateAction<CompanySponsorOnboardingProfileInfo>>;
  email: string | undefined;
  prefillCompanyName?: string | undefined;
  isPrefillReadonly?: boolean;
}

const fetchCompanySuggestions = async (email): Promise<string[]> => {
  const responseJson = await api.get("/onboarding/company_suggestions", { params: { email } });
  return responseJson.data?.company_suggestions || [];
};

const roleDropdownOptions = Object.values(SponsorRole).map((text) => ({
  label: text,
  value: text,
}));

function CompanySponsor({
  profileInfo,
  setProfileInfo,
  email,
  prefillCompanyName = "",
  isPrefillReadonly = true,
}: Props) {
  const { role, areasOfExpertise } = profileInfo;
  // Initialize input value from prefill if available and no company is selected
  const [companyInputValue, setCompanyInputValue] = useState<string>(
    prefillCompanyName && !profileInfo.company ? prefillCompanyName : ""
  );

  // Update input value when prefill changes or company gets selected
  useEffect(() => {
    if (prefillCompanyName && !profileInfo.company) {
      setCompanyInputValue(prefillCompanyName);
    } else if (profileInfo.company) {
      // Clear input value if company gets selected (react-select will show selected value)
      setCompanyInputValue("");
    }
  }, [prefillCompanyName, profileInfo.company]);

  const [companyOptions] = useCompanyOptions();

  const shouldFetchCompanySuggestions =
    !isPrefillReadonly && !profileInfo.company && Boolean(email);

  useQuery({
    queryKey: ["companySuggestions", email],
    queryFn: () => fetchCompanySuggestions(email),
    enabled: shouldFetchCompanySuggestions,
    onSuccess: (suggestions) => {
      if (!suggestions.length) return;
      setProfileInfo((prevProfileInfo) => ({
        ...prevProfileInfo,
        company: prevProfileInfo.company ?? suggestions[0],
      }));
    },
  });

  const handleCompany = (e, selection) => {
    if (selection?.action === "select-option" || selection?.action === "create-option") {
      setProfileInfo({
        ...profileInfo,
        company: e.label,
      });
      // Clear input value when an option is selected (react-select will show selected value instead)
      setCompanyInputValue("");
      track(SegmentEventName.CLICK, {
        ui_component: "Company Dropdown",
        react_component: "CompanySponsor",
        company: e.label,
      });
    } else if (selection?.action === "clear") {
      // Clear input value when selection is cleared
      setCompanyInputValue("");
    }
  };

  const handleCompanyInputChange = (inputValue: string, action: any) => {
    // Update input value for user typing, but don't clear it on blur/close
    // This allows the prefill text to remain visible until user selects or types
    if (action.action === "input-change") {
      setCompanyInputValue(inputValue);
    }
  };

  const handleCompanyWebsite = (e) => {
    setProfileInfo((oldProfileInfo) => ({
      ...oldProfileInfo,
      companyURL: e.target.value,
    }));
  };

  return (
    <>
      <S.DropdownRow>
        <S.DropdownTitle>Organization name</S.DropdownTitle>
        {isPrefillReadonly && prefillCompanyName && !profileInfo.company ? (
          <S.Input type="text" value={prefillCompanyName} tabIndex={-1} fixed readOnly />
        ) : (
          <SingleSelectDropdown
            withSearchIcon
            placeholder="Search organization names or add new"
            isCreatable
            options={companyOptions}
            onChange={handleCompany}
            value={getOptionFromValue(profileInfo.company)}
            inputValue={companyInputValue}
            onInputChange={handleCompanyInputChange}
            data-testid="company-sponsor-step-4-company-dropdown"
          />
        )}
      </S.DropdownRow>
      {profileInfo.company &&
        !companyOptions.map(({ label }) => label).includes(profileInfo.company) && (
          <S.DropdownRow>
            <S.DropdownTitle>Organization website</S.DropdownTitle>
            <TextInput
              placeholder="Enter organization website URL"
              onChange={handleCompanyWebsite}
              value={profileInfo.companyURL ?? ""}
              data-testid="company-sponsor-company-website-input"
              errors={[
                {
                  hasError:
                    Boolean(profileInfo?.companyURL?.length) &&
                    !isValidURL(profileInfo?.companyURL),
                  errorMessage: "Please enter a valid URL",
                },
              ]}
            />
          </S.DropdownRow>
        )}
      <S.DropdownRow>
        <S.DropdownTitle>Which of the following best describes your role?</S.DropdownTitle>
        <SingleSelectDropdown
          placeholder="Select your role"
          options={roleDropdownOptions}
          value={getOptionFromValue(role)}
          onChange={(e) => {
            track(SegmentEventName.CLICK, {
              ui_component: "Role Dropdown",
              react_component: "CompanySponsor",
              role: getValueFromOption(e),
            });
            setProfileInfo((oldProfileInfo) => ({
              ...oldProfileInfo,
              role: getValueFromOption(e),
            }));
          }}
          data-testid="ri-scientist-role-input"
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>What is your job title?</S.DropdownTitle>
        <TextInput
          value={profileInfo.title ?? ""}
          placeholder="Enter your job title"
          onChange={(e) => {
            setProfileInfo({
              ...profileInfo,
              title: e.target.value,
            });
          }}
          errors={[
            {
              hasError: (profileInfo.title?.length ?? 0) > COMPANY_SPONSOR_TITLE_LIMIT,
              errorMessage: `Job title must be less than ${COMPANY_SPONSOR_TITLE_LIMIT} characters`,
            },
          ]}
          hideClearButton
          data-testid="company-sponsor-step-4-title-field"
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>What is your expertise?</S.DropdownTitle>
        <KeywordsDropdown
          keywordType="area_of_expertise"
          targetType="sponsor"
          value={areasOfExpertise ?? []}
          onChange={(selection) => setProfileInfo({ ...profileInfo, areasOfExpertise: selection })}
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <PersonalizeHeader>
          <Icon name="Magic Line" size="md" color={COLORS.BLUE_500} />
          <PersonalizeTitle>Personalize your experience</PersonalizeTitle>
        </PersonalizeHeader>
        <SupportingText>
          Describe your interests to help us refine your solution and partner recommendations.
        </SupportingText>
        <DiscoverFeedInterests variant="onboarding" />
      </S.DropdownRow>
    </>
  );
}

export default CompanySponsor;

const PersonalizeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const PersonalizeTitle = styled.h2`
  ${FONTS.HEADING_5_SEMIBOLD};
  color: ${COLORS.NEUTRAL_800};
  margin: 0;
`;

const SupportingText = styled.p`
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_500};
  margin: 0 0 16px 0;
`;
